/* eslint-disable no-undef */
import React, { useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useExercisesBigQueryPublic } from "../../../hooks/useExercisesBigQuery";
import { useOrganizationDataByOrgId } from "../../../hooks/useOrganizationData";
import "./printable-training-plan.css";
import {
  buildPrescriptionSummary,
  getPrescriptionLabel,
} from "./utils/prescriptionFormatting";

const PrintableTrainingPlan = () => {
  const { id, organizationId } = useParams();
  const navigate = useNavigate();

  // Fetch exercises from BigQuery for name resolution (public access, no auth required)
  const {
    data: exercisesData,
    isLoading: exercisesLoading,
    error: exercisesError,
  } = useExercisesBigQueryPublic({
    search: "",
    type: "all",
    limit: 1000,
    offset: 0,
    enabled: true,
    organization_id: organizationId, // Pass the organization ID to get both global and org exercises
  });

  const exercisesMap = React.useMemo(() => {
    const exercises = exercisesData?.data?.exercises || [];
    const map = {};
    exercises.forEach((exercise) => {
      map[exercise.id] = exercise;
    });
    return map;
  }, [exercisesData]);

  const { organizationData, loading: organizationLoading } = useOrganizationDataByOrgId({ organizationId });
  const organizationName = organizationData?.name || "Organization";
  const brandingColors = organizationData?.brandingColors || null;

  // Get brightness to determine text color if not explicitly provided
  const getBrightness = (hexColor) => {
    if (!hexColor || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor))
      return 128;

    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Calculate brightness using a common formula
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  // Determine appropriate text color based on background color
  const getTextColor = (backgroundColor) => {
    return getBrightness(backgroundColor) > 140 ? "#000000" : "#ffffff";
  };

  // Brand colors handling
  const colors = {
    primary: "#5e35b1", // Default purple
    primaryLight: "#9162e4",
    secondary: "#ff6f00", // Default orange
    secondaryLight: "#ffad42",
    // Add text colors with fallbacks that calculate based on background color
    primaryText: getTextColor("#5e35b1"),
    primaryLightText: getTextColor("#9162e4"),
    secondaryText: getTextColor("#ff6f00"),
    secondaryLightText: getTextColor("#ffad42"),
    restDay: "#aaaaaa", // Default gray for rest days
    restDayText: "#444444", // Default text color for rest days
    ...brandingColors,
  };

  // Create CSS variables for colors
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--ptp-primary", colors.primary);
    root.style.setProperty("--ptp-primary-light", colors.primaryLight);
    root.style.setProperty("--ptp-secondary", colors.secondary);
    root.style.setProperty("--ptp-secondary-light", colors.secondaryLight);
    root.style.setProperty("--ptp-rest-day", colors.restDay || "#aaaaaa");

    // Add text color variables
    root.style.setProperty("--ptp-primary-text", colors.primaryText);
    root.style.setProperty("--ptp-primary-light-text", colors.primaryLightText);
    root.style.setProperty("--ptp-secondary-text", colors.secondaryText);
    root.style.setProperty(
      "--ptp-secondary-light-text",
      colors.secondaryLightText,
    );
    root.style.setProperty(
      "--ptp-rest-day-text",
      colors.restDayText || "#444444",
    );

    // Add class to body to enable printable-specific styling
    document.body.classList.add("ptp-printable-page");

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove("ptp-printable-page");
    };
  }, [colors]);

  const authToken =
    localStorage.getItem("authToken") ||
    (import.meta.env.VITE_API_KEY
      ? `Bearer ${import.meta.env.VITE_API_KEY}`
      : null);

  const [planData, setPlanData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchProtocolData = async () => {
      try {
        setIsLoading(true);
        const url = `${import.meta.env.VITE_API_BASE_URL || "https://app.mighty45.com/api/v1"}/organization/${organizationId}/protocol/${id}?includeSplits=true&includeExercises=true&includeClient=true`;

        const headers = {
          "Content-Type": "application/json",
        };

        if (authToken) {
          headers.Authorization = authToken;
        }

        const response = await fetch(url, {
          method: "GET",
          headers,
        });

        if (!response.ok) {
          let errorMessage = "Unable to load training plan";

          // Provide specific error messages based on status codes
          switch (response.status) {
            case 403:
              errorMessage =
                "This training plan is not publicly accessible. Only public training plans can be viewed through this link.";
              break;
            case 404:
              errorMessage =
                "This training plan was not found. It may have been deleted or the link is incorrect.";
              break;
            case 401:
              errorMessage =
                "You don't have permission to view this training plan. Please check if you have the correct access rights.";
              break;
            case 500:
              errorMessage =
                "There was a server error while loading the training plan. Please try again later.";
              break;
            default:
              // Try to get error message from response body if available
              try {
                const errorData = await response.json();
                if (errorData.error) {
                  errorMessage = errorData.error;
                } else {
                  errorMessage = `Error loading training plan (${response.status}: ${response.statusText})`;
                }
              } catch {
                errorMessage = `Error loading training plan (${response.status}: ${response.statusText})`;
              }
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();

        setPlanData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching protocol data:", error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    if (organizationId && id) {
      fetchProtocolData();
    } else {
      setError("Missing required props: organizationId or protocolId");
      setIsLoading(false);
    }
  }, [organizationId, id, authToken]);

  // Enhanced print handler for better mobile compatibility
  const handlePrint = () => {
    // Check if running on iOS device
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // On iOS, provide a hint message before proceeding
      const infoElement = document.createElement("div");
      infoElement.className = "ios-print-hint";
      infoElement.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; background: rgba(255,255,255,0.95); 
                  padding: 20px; z-index: 9999; text-align: center; box-shadow: 0 2px 10px rgba(0,0,0,0.2);">
        <p>On iOS, after clicking OK:</p>
        <p>1. Tap the share icon</p>
        <p>2. Scroll and select "Print"</p>
        <button style="margin-top: 10px; padding: 8px 16px; background: ${colors.primary}; color: ${colors.primaryText}; 
                       border: none; border-radius: 4px;">OK, Got it</button>
      </div>
    `;

      document.body.appendChild(infoElement);

      // Remove the hint when the button is clicked
      infoElement.querySelector("button").addEventListener("click", () => {
        document.body.removeChild(infoElement);
        // Proceed with normal print on iOS
        window.print();
      });
    } else {
      // Standard print functionality for non-iOS devices
      window.print();
    }
  };

  // Helper function to detect mobile browsers
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  };

  // Helper function to format exercise names
  const formatExerciseName = useCallback(
    (id) => {
      return exercisesMap[id].name;
    },
    [exercisesMap],
  );

  const getExercise = useCallback(
    (id) => {
      console.log("Getting exercise for ID:", id, exercisesMap[id]);
      return exercisesMap[id];
    },
    [exercisesMap],
  );

  // Helper function to check if a split is a rest day
  const isRestDay = (split) => {
    if (!split || !split.name) return true;
    return (
      split.isRestSplit ||
      split.name.toLowerCase().includes("rest") ||
      split.name.toLowerCase().includes("off") ||
      split.name.toLowerCase().includes("recovery")
    );
  };

  // Function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    // Handle Firestore timestamp format
    if (timestamp._seconds) {
      const date = new Date(timestamp._seconds * 1000);
      const options = { year: "numeric", month: "long", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    }
    
    // Handle regular Date or ISO string
    if (timestamp instanceof Date || typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        const options = { year: "numeric", month: "long", day: "numeric" };
        return date.toLocaleDateString(undefined, options);
      }
    }
    
    return "";
  };

  // Handle going back to regular view
  const handleBackToRegular = () => {
    navigate(`/plan/${organizationId}/${id}`);
  };

  if (isLoading || (exercisesLoading && !exercisesError)) {
    return <div className="ptp-loading">Loading your fitness plan...</div>;
  }

  if (error) {
    return (
      <div className="ptp-error-container">
        <div className="ptp-error-message">
          <h2>Training Plan Unavailable</h2>
          <p>{error}</p>
          <div className="ptp-error-help">
            <p>If you believe this is an error, please:</p>
            <ul>
              <li>Check that the link is correct</li>
              <li>Ensure the training plan is set to public</li>
              <li>Contact support if the problem persists</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="ptp-error-container">
        <div className="ptp-error-message">
          <h2>No Plan Data Available</h2>
          <p>Could not load the fitness plan. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ptp-printable-container">
      <div className="ptp-print-controls no-print">
        <button onClick={handleBackToRegular} className="ptp-back-button">
          Back to Regular View
        </button>
        <button
          onClick={handlePrint}
          className="ptp-print-button"
          style={{ backgroundColor: colors.primary, color: colors.primaryText }}
        >
          {isMobileDevice() ? "Export PDF/Print" : "Print"}
        </button>
      </div>

      <div className="ptp-document">
        {/* Combined Cover and Overview Page */}
        <div className="ptp-combined-page">
          {/* Cover Content */}
          <div className="ptp-cover-content">
            <h1 className="ptp-plan-name" style={{ color: colors.primary }}>
              {planData.name}
            </h1>
            <h2 className="ptp-org-name" style={{ color: colors.secondary }}>
              {organizationName}
            </h2>
            <p className="ptp-plan-type">
              A {planData.difficulty} level plan for {planData.type} fitness
            </p>
            <div className="ptp-cover-info">
              <p>Created: {formatDate(planData.updatedOn)}</p>
            </div>
          </div>

          {/* Overview Content - Only show if there is data */}
          {planData.description && planData.description.length > 0 && (
            <div className="ptp-plan-overview">
              <h2
                className="ptp-section-title"
                style={{
                  color: colors.primary,
                  borderBottomColor: colors.secondary,
                }}
              >
                Plan Overview
              </h2>
              <div className="ptp-plan-description">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {planData.description}
                </ReactMarkdown>
              </div>

              {planData.eqipment_needed &&
                planData.eqipment_needed.length > 0 && (
                  <div
                    className="ptp-equipment-section"
                    style={{
                      backgroundColor: colors.primaryLight,
                      color: colors.primaryLightText,
                    }}
                  >
                    <h3
                      className="ptp-subsection-title"
                      style={{ color: colors.primaryLightText }}
                    >
                      Equipment Needed
                    </h3>
                    <ul className="ptp-equipment-list">
                      {planData.eqipment_needed.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {planData.getting_started && (
                <div className="ptp-getting-started">
                  <h3
                    className="ptp-subsection-title"
                    style={{ color: colors.secondary }}
                  >
                    Getting Started
                  </h3>
                  <p>{planData.getting_started}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Workout Content - Without fixed page breaks */}
        <div className="ptp-content">
          <h2
            className="ptp-section-title"
            style={{
              color: colors.primary,
              borderBottomColor: colors.secondary,
            }}
          >
            Workout Rotation
          </h2>

          <div className="ptp-workout-list">
            {planData.splits &&
              planData.splits.map((split, index) => {
                // Determine if this is a rest day
                const restDay = isRestDay(split);

                // Determine header background color with alternating pattern
                let headerBgColor;
                let textColor;

                if (restDay) {
                  // Rest day gets a consistent gray color
                  headerBgColor = colors.restDay || "#aaaaaa";
                  textColor = colors.restDayText || "#444444";
                } else {
                  // Alternate between primary and secondary colors for workout days
                  const isEven = index % 2 === 0;
                  if (isEven) {
                    headerBgColor = colors.primaryLight;
                    textColor = colors.primaryLightText;
                  } else {
                    headerBgColor = colors.secondaryLight;
                    textColor = colors.secondaryLightText;
                  }
                }

                return (
                  <div
                    key={split.id}
                    className={`ptp-workout-card ${
                      restDay ? "ptp-rest-day" : "ptp-workout-day"
                    }`}
                  >
                    <div
                      className="ptp-workout-header"
                      style={{
                        backgroundColor: headerBgColor,
                        color: textColor,
                      }}
                    >
                      <span className="ptp-day-number">Day {index + 1}:</span>{" "}
                      {split.name || "Rest Day"}
                    </div>
                    <div className="ptp-workout-body">
                      <p className="ptp-workout-description">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {split.description
                            ? split.description
                            : restDay
                              ? "Focus on recovery, rest, and regeneration."
                              : "Focus on proper form and controlled movements."}
                        </ReactMarkdown>
                      </p>

                      {!restDay && !split.isRestSplit ? (
                        <>
                          {split.warmup && (
                            <div className="ptp-warmup">
                              <strong>Warm-up:</strong> {split.warmup}
                            </div>
                          )}

                          {split.exercises && split.exercises.length > 0 && (
                            <div className="ptp-exercise-list">
                              {split.exercises.map((exercise, exerciseIndex) => {
                                const exerciseSummary =
                                  buildPrescriptionSummary(exercise);
                                const exercisePrescriptionLabel =
                                  getPrescriptionLabel(exerciseSummary.type);

                                return (
                                  <div
                                    className="ptp-exercise-item"
                                    key={exercise.id || exerciseIndex}
                                  >
                                    {exercise.type === "super_set" ? (
                                      <>
                                        <div className="ptp-superset-header">
                                          <span
                                            className="ptp-superset-name"
                                            style={{
                                              color:
                                                index % 2 === 0
                                                  ? colors.primary
                                                  : colors.secondary,
                                            }}
                                          >
                                            SUPERSET: {exercise.superSetName}
                                          </span>
                                          <span className="ptp-detail">
                                            {exercisePrescriptionLabel}
                                          </span>
                                          {exerciseSummary.summaryChips.length >
                                            0 && (
                                            <span className="ptp-detail">
                                              {exerciseSummary.summaryChips.join(
                                                " • ",
                                              )}
                                            </span>
                                          )}
                                          {exerciseSummary.detailRows.length >
                                            0 && (
                                            <span className="ptp-detail">
                                              {exerciseSummary.detailRows
                                                .map(
                                                  (row) =>
                                                    `${row.label}: ${row.value}`,
                                                )
                                                .join(" • ")}
                                            </span>
                                          )}
                                        </div>

                                        <div className="ptp-superset-exercises">
                                          {exercise.exercises.map(
                                            (subExercise, subIndex) => {
                                              const subSummary =
                                                buildPrescriptionSummary(
                                                  subExercise,
                                                );
                                              const subPrescriptionLabel =
                                                getPrescriptionLabel(
                                                  subSummary.type,
                                                );

                                              return (
                                                <div
                                                  key={
                                                    subExercise.id ||
                                                    `sub-${subIndex}`
                                                  }
                                                  className="ptp-superset-exercise"
                                                >
                                                  <span
                                                    className="ptp-exercise-name"
                                                    style={{
                                                      color: colors.primary,
                                                    }}
                                                  >
                                                    {formatExerciseName(
                                                      subExercise.exercise,
                                                    )}
                                                  </span>

                                                  <div className="ptp-exercise-details">
                                                    <span className="ptp-detail">
                                                      {subPrescriptionLabel}
                                                    </span>
                                                {subSummary.summaryChips.map(
                                                  (chip, chipIndex) => (
                                                        <span
                                                      key={`${chip}-${chipIndex}`}
                                                          className="ptp-detail"
                                                        >
                                                          {chip}
                                                        </span>
                                                      ),
                                                    )}
                                                    {subSummary.detailRows.map(
                                                      (row) => (
                                                        <span
                                                          key={`${row.label}-${row.value}`}
                                                          className="ptp-detail"
                                                        >
                                                          {row.label}:{" "}
                                                          {row.value}
                                                        </span>
                                                      ),
                                                    )}
                                                  </div>

                                                  {subExercise.notes && (
                                                    <div className="ptp-notes">
                                                      <strong>Notes:</strong>{" "}
                                                      <ReactMarkdown
                                                        remarkPlugins={[
                                                          remarkGfm,
                                                        ]}
                                                      >
                                                        {subExercise.notes}
                                                      </ReactMarkdown>
                                                    </div>
                                                  )}

                                                  {/* Sub-exercise Images */}
                                                  {getExercise(
                                                    subExercise.exercise,
                                                  )?.images &&
                                                    getExercise(
                                                      subExercise.exercise,
                                                    ).images.length > 0 && (
                                                      <div className="ptp-exercise-images">
                                                        <strong>
                                                          Exercise Images:
                                                        </strong>
                                                        <div className="ptp-images-grid">
                                                          {getExercise(
                                                            subExercise.exercise,
                                                          ).images.map(
                                                            (
                                                              image,
                                                              imageIndex,
                                                            ) => (
                                                              <img
                                                                key={imageIndex}
                                                                src={image}
                                                                alt={`${formatExerciseName(subExercise.exercise)} demonstration ${imageIndex + 1}`}
                                                                className="ptp-exercise-image"
                                                              />
                                                            ),
                                                          )}
                                                        </div>
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <span
                                          className="ptp-exercise-name"
                                          style={{ color: colors.primary }}
                                        >
                                          {formatExerciseName(exercise.exercise)}
                                        </span>

                                        <div className="ptp-exercise-details">
                                          <span className="ptp-detail">
                                            {exercisePrescriptionLabel}
                                          </span>
                                      {exerciseSummary.summaryChips.map(
                                        (chip, chipIndex) => (
                                              <span
                                            key={`${chip}-${chipIndex}`}
                                                className="ptp-detail"
                                              >
                                                {chip}
                                              </span>
                                            ),
                                          )}
                                          {exerciseSummary.detailRows.map(
                                            (row) => (
                                              <span
                                                key={`${row.label}-${row.value}`}
                                                className="ptp-detail"
                                              >
                                                {row.label}: {row.value}
                                              </span>
                                            ),
                                          )}
                                        </div>

                                        {exercise.notes && (
                                          <div className="ptp-notes">
                                            <strong>Notes:</strong>{" "}
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                            >
                                              {exercise.notes}
                                            </ReactMarkdown>
                                          </div>
                                        )}

                                        {getExercise(exercise.exercise)
                                          .description && (
                                          <div className="ptp-notes">
                                            <strong>Description:</strong>{" "}
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                            >
                                              {
                                                getExercise(exercise.exercise)
                                                  .description
                                              }
                                            </ReactMarkdown>
                                          </div>
                                        )}

                                        {getExercise(exercise.exercise)
                                          .instructions && (
                                          <div className="ptp-notes">
                                            <strong>Instructions:</strong>{" "}
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                            >
                                              {
                                                getExercise(exercise.exercise)
                                                  .instructions
                                              }
                                            </ReactMarkdown>
                                          </div>
                                        )}

                                        {/* Exercise Images */}
                                        {getExercise(exercise.exercise)
                                          ?.images &&
                                          getExercise(exercise.exercise).images
                                            .length > 0 && (
                                          <div className="ptp-exercise-images">
                                            <strong>Exercise Images:</strong>
                                            <div className="ptp-images-grid">
                                              {getExercise(
                                                exercise.exercise,
                                              ).images.map(
                                                (image, imageIndex) => (
                                                  <img
                                                    key={imageIndex}
                                                    src={image}
                                                    alt={`${formatExerciseName(exercise.exercise)} demonstration ${imageIndex + 1}`}
                                                    className="ptp-exercise-image"
                                                  />
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Alternative Exercises */}
                                        {(exercise.alternatives || []).length > 0 && (
                                          <div className="ptp-alternatives">
                                            <div className="ptp-alternatives-label">
                                              ⇄ Alternative exercises
                                            </div>
                                            {(exercise.alternatives || []).map((alt, altIdx) => {
                                              const altSummary = buildPrescriptionSummary(alt);
                                              const altPrescriptionLabel = getPrescriptionLabel(altSummary.type);
                                              return (
                                                <div key={alt.id || altIdx} className="ptp-alternative-item">
                                                  {alt.type === "super_set" ? (
                                                    <>
                                                      <span className="ptp-alternative-name" style={{ color: colors.primary }}>
                                                        SUPERSET: {alt.superSetName}
                                                      </span>
                                                      <span className="ptp-detail">{altPrescriptionLabel}</span>
                                                      {(alt.exercises || []).map((child, childIdx) => {
                                                        const childSummary = buildPrescriptionSummary(child);
                                                        return (
                                                          <div key={child.id || childIdx} className="ptp-alternative-sub">
                                                            <span className="ptp-alternative-name" style={{ color: colors.primary }}>
                                                              {formatExerciseName(child.exercise)}
                                                            </span>
                                                            <span className="ptp-detail">
                                                              {getPrescriptionLabel(childSummary.type)}
                                                              {childSummary.summaryChips.map((chip, i) => (
                                                                <span key={i} className="ptp-detail">{chip}</span>
                                                              ))}
                                                            </span>
                                                          </div>
                                                        );
                                                      })}
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="ptp-alternative-name" style={{ color: colors.primary }}>
                                                        {formatExerciseName(alt.exercise)}
                                                      </span>
                                                      <div className="ptp-exercise-details">
                                                        <span className="ptp-detail">{altPrescriptionLabel}</span>
                                                        {altSummary.summaryChips.map((chip, i) => (
                                                          <span key={i} className="ptp-detail">{chip}</span>
                                                        ))}
                                                        {altSummary.detailRows.map((row) => (
                                                          <span key={`${row.label}-${row.value}`} className="ptp-detail">
                                                            {row.label}: {row.value}
                                                          </span>
                                                        ))}
                                                      </div>
                                                      {alt.notes && (
                                                        <div className="ptp-notes">
                                                          <strong>Notes:</strong>{" "}
                                                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{alt.notes}</ReactMarkdown>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="ptp-rest-content">
                          <p>
                            Recovery is important! Take this day to let your
                            body rest and rebuild.
                          </p>
                          <p>
                            Consider light activities like walking or gentle
                            stretching if you wish to stay active.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* FAQ Section */}
          {planData.faqs_and_answers &&
            planData.faqs_and_answers.length > 0 && (
              <div className="ptp-faq-section">
                <h2
                  className="ptp-section-title"
                  style={{
                    color: colors.primary,
                    borderBottomColor: colors.secondary,
                  }}
                >
                  Frequently Asked Questions
                </h2>
                <div className="ptp-faq-items">
                  {planData.faqs_and_answers.map((faq, index) => (
                    <div className="ptp-faq-item" key={index}>
                      <p
                        className="ptp-faq-question"
                        style={{ color: colors.primary }}
                      >
                        {faq.q}
                      </p>
                      <p className="ptp-faq-answer">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Simple footer */}
          <div
            className="ptp-footer"
            style={{ borderTopColor: colors.primary }}
          >
            <p>
              {planData.name} | {organizationName}
            </p>
            <p>Created: {formatDate(planData.createdOn || planData.updatedOn)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableTrainingPlan;
